import React from 'react';
import { InputLabel } from 'src/common';
import styled from 'styled-components';
import { FrameworksSelect, FrameworksControlView } from '.';
import { ReactComponent as CrossIcon } from 'src/assets/cross.svg';
import { ControlFrameworksListQuery } from 'src/graphql/_generated';

const RowContainer = styled.div<{ $canRemove: boolean }>`
  display: grid;
  grid-template-columns: ${({ $canRemove }) => ($canRemove ? '2fr 1fr auto' : '2fr 1fr')};
  grid-gap: 21px;
  align-items: start;

  .input-column {
    display: flex;
    flex-direction: column;
  }

  .remove {
    display: flex;
    align-items: flex-start;
    padding-top: 24px;

    &__label {
      user-select: none;
      opacity: 0;
    }

    &__button {
      display: flex;
      align-items: center;
      justify-content: center;
      cursor: pointer;
      height: 54px;
      width: 14px;

      &:hover {
        svg {
          color: ${({ theme }) => theme.colors.error};
        }
      }

      svg {
        width: 20px;
        height: 20px;
        color: ${({ theme }) => theme.colors.prussianBlue};
        transition: color 0.2s ease;
      }
    }
  }

  ${({ theme }) => theme.breakpoints.down('md')} {
    grid-template-columns: 1fr auto;
    grid-template-rows: auto auto;

    .input-column:first-child {
      grid-column: 1;
      grid-row: 1;
    }

    .input-column:nth-child(2) {
      grid-column: 1;
      grid-row: 2;
    }

    .remove {
      grid-column: 2;
      grid-row: 1;
      padding-top: 24px;
      align-self: start;

      &__button {
        height: 32px;
      }
    }
  }
`;

interface RowProps {
  idx: number;
  controlId?: string;
  controlTouched?: boolean;
  onControlChange: (idx: number, controlId: string) => void;
  onControlBlur: (idx: number) => void;
  onRemove: (idx: number) => void;
  canRemove: boolean;
  isLoading?: boolean;
  data?: ControlFrameworksListQuery;
}

export const Row: React.FC<RowProps> = ({
  idx,
  controlId,
  controlTouched,
  onControlChange,
  onControlBlur,
  onRemove,
  canRemove,
  isLoading,
  data,
}) => {
  const controlError = controlTouched && !controlId;
  return (
    <RowContainer $canRemove={canRemove}>
      <div className="input-column">
        <InputLabel>SOURCE FRAMEWORK</InputLabel>
        <FrameworksSelect
          name={`frameworkControl-${idx}`}
          dataCy={`framework-control-input-${idx}`}
          value={controlId}
          onChange={(o: { value: string }) => {
            onControlChange(idx, o.value);
          }}
          onBlur={() => onControlBlur(idx)}
          error={controlError}
          helperText={controlError ? 'Required' : undefined}
          isLoading={isLoading}
          data={data}
          isDisabled={!canRemove}
        />
      </div>
      <div className="input-column">
        <InputLabel>FRAMEWORK CONTROL ID</InputLabel>
        <FrameworksControlView controlId={controlId} isLoading={isLoading} isDisabled={!canRemove} />
      </div>
      {canRemove && (
        <div className="remove">
          <InputLabel className="remove__label">X</InputLabel>
          <div className="remove__button">
            {!isLoading && (
              <div onClick={() => onRemove(idx)} data-cy={`remove-framework-control-${idx}`}>
                <CrossIcon />
              </div>
            )}
          </div>
        </div>
      )}
    </RowContainer>
  );
};
